// apps/web/src/render/print-mode-switcher.tsx
//
// Phase 11.8 — REND-01 (Strand 3) the .print-mode-switcher popover. An icon-only
// Printer trigger opens a popover listing one menuitemradio row per
// recipe.printModes[i] (labelled from printModes[i].label DATA). Selecting a row
// sets the active mode id — written as a generic string key in the spread-local
// SpreadOptions.printOptions when a write path is supplied (the spread context),
// else it drives the lifted component state alone (the single-recipe view route).
//
// It is a .no-print ON-SCREEN control — suppressed on the printed pages (the
// print-root omits all controls). The ACTIVE row carries aria-checked/aria-current
// + a NON-COLOR signal (a leading check glyph AND font-semibold) — the active mode
// is NEVER distinguished by color alone (UI-SPEC Color item 3 / a11y item 3).
//
// C1 generality (scripts/grep-no-recipe-identifiers.sh): mode labels flow from
// printModes[] DATA only; the switcher holds NO mode/category/flavor literal.
//
// XSS discipline (T-11.8-XSS): the mode label is a JSX text child (React
// auto-escapes). No dangerouslySetInnerHTML.
import type { PrintMode, SpreadOptions } from "@the-recipe-spread/shared";
import { Check, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// The generic string key the active mode id is stored under in the spread-local
// printOptions record. A non-domain key — printOptions is an untyped
// Record<string, unknown> (no schema/enum change).
const ACTIVE_MODE_KEY = "activeModeId";

/** Resolve the active mode id from the spread-local printOptions record (a
 *  generic string-key read). Returns undefined when absent — the caller defaults
 *  to printModes[0]. */
export function readActiveModeId(printOptions: SpreadOptions["printOptions"]): string | undefined {
  const value = printOptions?.[ACTIVE_MODE_KEY];
  return typeof value === "string" ? value : undefined;
}

/** Produce the next printOptions record with the active mode id written under the
 *  generic key (no mutation; no schema change). */
export function writeActiveModeId(
  printOptions: SpreadOptions["printOptions"],
  modeId: string,
): Record<string, unknown> {
  return { ...(printOptions ?? {}), [ACTIVE_MODE_KEY]: modeId };
}

export interface PrintModeSwitcherProps {
  // The recipe-declared print modes (the rows). Absent / empty → the trigger is
  // hidden (nothing to choose).
  modes: ReadonlyArray<PrintMode>;
  // The currently-active mode id (lifted by the caller). Absent / drifted → the
  // first mode is treated as active.
  activeModeId?: string | undefined;
  // Called with the chosen mode id when a row is selected. The caller lifts this
  // into component state AND (in the spread context) persists it via
  // writeActiveModeId into SpreadOptions.printOptions.
  onSelect(modeId: string): void;
}

export function PrintModeSwitcher({
  modes,
  activeModeId,
  onSelect,
}: PrintModeSwitcherProps): React.ReactElement | null {
  // Gate: no modes → no trigger (nothing to choose). The print-root is likewise
  // gated, so this matches the no-print-surface case.
  if (modes.length === 0) return null;

  const firstMode = modes[0] as PrintMode;
  const effectiveActiveId = modes.some((m) => m.id === activeModeId) ? activeModeId : firstMode.id;

  return (
    <div className="no-print">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Choose print layout">
            <Printer size={16} aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56">
          {/* A menu of mutually-exclusive print layouts: role="menu" with
              menuitemradio rows per the UI-SPEC. A native <select> would not
              carry the popover affordance. (No biome-ignore needed —
              useSemanticElements does not flag role="menu".) */}
          <div role="menu" aria-label="Print layout" className="flex flex-col gap-1">
            {modes.map((mode) => {
              const isActive = mode.id === effectiveActiveId;
              return (
                <button
                  key={mode.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => onSelect(mode.id)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)] hover:bg-[var(--recipe-accent-soft)] ${
                    isActive ? "font-semibold" : "font-normal"
                  }`}
                >
                  {/* NON-COLOR active signal: a leading check glyph (plus the
                      font-semibold weight above) — never color alone. */}
                  <Check size={14} aria-hidden className={isActive ? "opacity-100" : "opacity-0"} />
                  <span>{mode.label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
PrintModeSwitcher.displayName = "PrintModeSwitcher";
